/**
 * Merge attack-initiated-minimal / attack-complete-minimal WS patches into client game state.
 */

import { inferWinnerFromZeroGeneralHealth } from './inferWinnerFromGeneralHealth.js';

function ensurePlayerState(gameState, playerId) {
  if (!gameState.state || typeof gameState.state !== 'object') {
    throw new Error('mergeAttackWsPatch: gameState.state is required');
  }
  const key = resolvePlayerStateKey(gameState, playerId);
  if (!key) {
    throw new Error(`mergeAttackWsPatch: missing player state for ${playerId}`);
  }
  return gameState.state[key];
}

function resolvePlayerStateKey(gameState, playerId) {
  if (!gameState?.state || playerId == null) {
    return null;
  }
  if (gameState.state[playerId]) {
    return playerId;
  }
  const pid = String(playerId);
  return Object.keys(gameState.state).find((key) => String(key) === pid) ?? null;
}

/**
 * Winner inference is a best-effort safety net (server sends the authoritative winner). It must
 * never throw a merge: when players cannot be resolved (fewer than 2), skip inference entirely.
 * @returns {string[]|null} resolved player ids, or null when inference is not possible
 */
function resolveInferencePlayers(merged) {
  if (Array.isArray(merged.players) && merged.players.length >= 2) {
    return merged.players;
  }
  const stateKeys = Object.keys(merged.state || {});
  if (stateKeys.length >= 2) {
    return stateKeys;
  }
  return null;
}

function applyGameStateMeta(merged, meta, prevGameState) {
  merged.stateVersion = meta.stateVersion;
  merged.turn = meta.turn;
  merged.winner = meta.winner ?? prevGameState.winner ?? null;
  if (merged.winner != null) {
    merged.gameEnded = true;
  }
  merged.campaignMode = meta.campaignMode === true;
  merged.missionId = meta.missionId ?? prevGameState.missionId ?? null;
  if (meta.isFriendlyMatch === true) {
    merged.isFriendlyMatch = true;
  } else if (meta.isFriendlyMatch === false) {
    merged.isFriendlyMatch = false;
  }
}

function applyBoardPatches(merged, boardPatches, { requireUnit = true } = {}) {
  if (!Array.isArray(boardPatches)) {
    return;
  }
  for (const patch of boardPatches) {
    if (!patch || typeof patch !== 'object') {
      throw new Error('mergeAttackWsPatch: invalid board patch');
    }
    const { playerId, zone, index, unit } = patch;
    if (playerId == null || zone == null || typeof index !== 'number') {
      throw new Error('mergeAttackWsPatch: board patch missing required fields');
    }
    if (requireUnit && !unit) {
      throw new Error('mergeAttackWsPatch: board patch requires unit');
    }
    const playerKey = resolvePlayerStateKey(merged, playerId);
    if (!playerKey) {
      throw new Error(`mergeAttackWsPatch: missing player state for ${playerId}`);
    }
    const playerState = merged.state[playerKey];
    if (!playerState.playedOrders || typeof playerState.playedOrders !== 'object') {
      throw new Error(`mergeAttackWsPatch: player ${playerId} missing playedOrders`);
    }
    const row = Array.isArray(playerState.playedOrders[zone])
      ? [...playerState.playedOrders[zone]]
      : [];
    row[index] = unit ?? null;
    merged.state[playerKey] = {
      ...playerState,
      playedOrders: {
        ...playerState.playedOrders,
        [zone]: row,
      },
    };
  }
}

function applyPlayerResourcePatches(merged, playerResourcePatches) {
  if (!Array.isArray(playerResourcePatches)) {
    return;
  }
  for (const patch of playerResourcePatches) {
    if (!patch || typeof patch !== 'object' || patch.playerId == null) {
      throw new Error('mergeAttackWsPatch: invalid player resource patch');
    }
    const playerKey = resolvePlayerStateKey(merged, patch.playerId);
    if (!playerKey) {
      throw new Error(`mergeAttackWsPatch: missing player state for ${patch.playerId}`);
    }
    const playerState = merged.state[playerKey];
    merged.state[playerKey] = {
      ...playerState,
      ...(typeof patch.health === 'number' ? { health: patch.health } : {}),
      ...(typeof patch.maxHealth === 'number' ? { maxHealth: patch.maxHealth } : {}),
      ...(typeof patch.intelligence === 'number' ? { intelligence: patch.intelligence } : {}),
      ...(typeof patch.maxIntelligence === 'number' ? { maxIntelligence: patch.maxIntelligence } : {}),
    };
  }
}

function applyDronePatches(merged, dronePatches) {
  if (!Array.isArray(dronePatches)) {
    return;
  }
  for (const patch of dronePatches) {
    if (!patch || typeof patch !== 'object' || patch.playerId == null) {
      throw new Error('mergeAttackWsPatch: invalid drone patch');
    }
    const playerKey = resolvePlayerStateKey(merged, patch.playerId);
    if (!playerKey) {
      throw new Error(`mergeAttackWsPatch: missing player state for ${patch.playerId}`);
    }
    if (!Array.isArray(patch.drones)) {
      throw new Error('mergeAttackWsPatch: drone patch requires drones array');
    }
    merged.state[playerKey] = {
      ...merged.state[playerKey],
      drones: patch.drones.map((drone) => ({ ...drone })),
    };
  }
}

function applyPlayerCombatPatches(merged, playerCombatPatches) {
  if (!Array.isArray(playerCombatPatches)) {
    return;
  }
  for (const patch of playerCombatPatches) {
    if (!patch || typeof patch !== 'object' || patch.playerId == null) {
      throw new Error('mergeAttackWsPatch: invalid player combat patch');
    }
    const playerKey = resolvePlayerStateKey(merged, patch.playerId);
    if (!playerKey) {
      throw new Error(`mergeAttackWsPatch: missing player state for ${patch.playerId}`);
    }
    const playerState = merged.state[playerKey];
    merged.state[playerKey] = {
      ...playerState,
      ...(patch.barriersRemoved !== undefined ? { barriersRemoved: patch.barriersRemoved } : {}),
      ...(Array.isArray(patch.generalInteractions)
        ? { generalInteractions: patch.generalInteractions.map((entry) => ({ ...entry })) }
        : {}),
      ...(Array.isArray(patch.completedOrders)
        ? { completedOrders: patch.completedOrders.map((entry) => (entry ? { ...entry } : entry)) }
        : {}),
    };
  }
}

/** One-shot VFX queues — consumed at WS handler entry via resolveWsAttackVfxFragment, never persisted. */
const EPHEMERAL_VFX_KEYS = [
  'pendingDeployAnimations',
  'pendingHandOrderGrantFlyAnimations',
  'pendingTerfaShadowSniperFlyAnimations',
  'pendingVeilcasterWeaponStealFlyAnimations',
  'pendingHarralSpawnbladeWeaponGrantFlyAnimations',
  'pendingArranAbilityStealFlyAnimations',
  'pendingHarralMindLeechUnitStealFlyAnimations',
  'pendingCaropaeraRedeemGhostAnimations',
  'pendingTravelingSalesmanCheaperHandAnimations',
  'pendingNeurotoxShrinkPulseAnimations',
  'pendingDroneDamageAnimations',
  'pendingRedeemDeathSequences',
  'pendingRaidAnimations',
  'pendingMaxIntelIncreaseAnimations',
  'pendingStrategemAnimations',
  'strategemAnimations',
  // Professional Bully (291): WS entry consumer + server clearEphemeralBullyDamageAnnotation
  'bullyDamageInstanceIds',
];

function stripEphemeralVfxKeys(merged) {
  if (!merged || typeof merged !== 'object') {
    throw new Error('stripEphemeralVfxKeys: merged game state is required');
  }
  for (const key of EPHEMERAL_VFX_KEYS) {
    if (key in merged) {
      delete merged[key];
    }
  }
}

function mergeMinimalAttackPatch(prevGameState, msg, syncType) {
  if (!prevGameState || typeof prevGameState !== 'object') {
    throw new Error(`mergeAttackWsPatch: previous game state is required for ${syncType}`);
  }
  if (!msg.gameStateMeta || typeof msg.gameStateMeta !== 'object') {
    throw new Error(`mergeAttackWsPatch: gameStateMeta is required for ${syncType}`);
  }

  const merged = {
    ...prevGameState,
    state: { ...prevGameState.state },
  };
  applyGameStateMeta(merged, msg.gameStateMeta, prevGameState);
  applyBoardPatches(merged, msg.boardPatches, {
    requireUnit: syncType === 'attack-initiated-minimal',
  });
  applyPlayerResourcePatches(merged, msg.playerResourcePatches);
  applyDronePatches(merged, msg.dronePatches);
  if (syncType === 'attack-complete-minimal') {
    applyPlayerCombatPatches(merged, msg.playerCombatPatches);
    stripEphemeralVfxKeys(merged);
    if (merged.winner == null) {
      const inferencePlayers = resolveInferencePlayers(merged);
      if (inferencePlayers) {
        if (!Array.isArray(merged.players) || merged.players.length < 2) {
          merged.players = inferencePlayers;
        }
        const inferredWinner = inferWinnerFromZeroGeneralHealth(merged);
        if (inferredWinner != null) {
          merged.winner = inferredWinner;
          merged.gameEnded = true;
        }
      }
    }
  }
  return merged;
}

export function mergeAttackInitiatedWsPatch(prevGameState, msg) {
  if (!msg || typeof msg !== 'object') {
    throw new Error('mergeAttackInitiatedWsPatch: msg is required');
  }
  if (msg.gameStateSync !== 'attack-initiated-minimal') {
    if (!msg.gameState || typeof msg.gameState !== 'object') {
      throw new Error('mergeAttackInitiatedWsPatch: legacy attack-initiated requires gameState');
    }
    return msg.gameState;
  }
  return mergeMinimalAttackPatch(prevGameState, msg, 'attack-initiated-minimal');
}

export function mergeAttackCompleteWsPatch(prevGameState, msg) {
  if (!msg || typeof msg !== 'object') {
    throw new Error('mergeAttackCompleteWsPatch: msg is required');
  }
  if (msg.gameStateSync !== 'attack-complete-minimal') {
    if (!msg.gameState || typeof msg.gameState !== 'object') {
      throw new Error('mergeAttackCompleteWsPatch: legacy attack-complete requires gameState');
    }
    return msg.gameState;
  }
  return mergeMinimalAttackPatch(prevGameState, msg, 'attack-complete-minimal');
}

export function mergeAttackWsMessage(prevGameState, msg) {
  if (!msg || typeof msg !== 'object') {
    throw new Error('mergeAttackWsMessage: msg is required');
  }
  if (msg.gameStateSync === 'attack-initiated-minimal') {
    return mergeAttackInitiatedWsPatch(prevGameState, msg);
  }
  if (msg.gameStateSync === 'attack-complete-minimal') {
    return mergeAttackCompleteWsPatch(prevGameState, msg);
  }
  if (!msg.gameState || typeof msg.gameState !== 'object') {
    throw new Error('mergeAttackWsMessage: message requires gameState or minimal attack sync marker');
  }
  return msg.gameState;
}

/**
 * Fragment for early WS handler VFX consumers (pending* arrays + bullyDamageInstanceIds).
 * GUARDRAIL (291): bully ids from ephemeralVfx on attack-complete-minimal; consumed at WS entry
 * or attack-complete handler — never left in merged React state (EPHEMERAL_VFX_KEYS).
 */
export function resolveWsAttackVfxFragment(msg) {
  if (!msg || typeof msg !== 'object') {
    return null;
  }
  if (msg.gameState && typeof msg.gameState === 'object') {
    return msg.gameState;
  }
  if (msg.gameStateSync === 'attack-complete-minimal' && msg.ephemeralVfx) {
    return { ...msg.ephemeralVfx };
  }
  return null;
}
